/**
 * Token Rotation Service
 * 
 * Handles automatic token refresh and rotation with secure storage updates.
 * Integrates with the secure token storage to maintain valid authentication tokens.
 */

import { secureTokenStorage, type StoredTokenData } from './SecureTokenStorage';

/**
 * Token rotation configuration
 */
export interface TokenRotationConfig {
  // Refresh settings
  enableAutoRefresh: boolean;
  refreshThreshold: number; // milliseconds before expiration to refresh
  refreshRetryAttempts: number;
  refreshRetryDelay: number; // milliseconds
  
  // Rotation settings
  enableTokenRotation: boolean;
  rotationInterval: number; // milliseconds
  maxTokenAge: number; // milliseconds
  
  // Network settings
  refreshEndpoint: string;
  timeoutDuration: number; // milliseconds
  
  // Security settings
  enableSecureRefresh: boolean;
  validateRefreshResponse: boolean;
  
  // Monitoring settings
  enableRefreshMonitoring: boolean;
  enableDebugLogging: boolean;
}

/**
 * Token refresh events
 */
export type TokenRefreshEvent = 
  | { type: 'refresh_started'; data: { reason: string; timestamp: string } }
  | { type: 'refresh_completed'; data: { duration: number; newExpiresAt: string } }
  | { type: 'refresh_failed'; data: { error: string; attempt: number; willRetry: boolean } }
  | { type: 'rotation_completed'; data: { oldTokenId?: string; newTokenId?: string } }
  | { type: 'token_expired'; data: { expiredAt: string; action: 'refresh' | 'logout' } }
  | { type: 'refresh_threshold_reached'; data: { expiresIn: number; willRefresh: boolean } };

export type TokenRefreshEventListener = (event: TokenRefreshEvent) => void;

/**
 * Token refresh response structure
 */
export interface TokenRefreshResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

/**
 * Token Rotation Service
 */
export class TokenRotationService {
  private config: TokenRotationConfig;
  private eventListeners: Set<TokenRefreshEventListener> = new Set();
  private isInitialized = false;
  
  // Refresh state
  private refreshTimer: NodeJS.Timeout | null = null;
  private rotationTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private refreshAttempts = 0;
  private lastRefreshTime: string | null = null;

  constructor(config: Partial<TokenRotationConfig> = {}) {
    this.config = {
      enableAutoRefresh: true,
      refreshThreshold: 300000, // 5 minutes
      refreshRetryAttempts: 3,
      refreshRetryDelay: 5000, // 5 seconds
      enableTokenRotation: true,
      rotationInterval: 3600000, // 1 hour
      maxTokenAge: 86400000, // 24 hours
      refreshEndpoint: '/auth/refresh',
      timeoutDuration: 10000, // 10 seconds
      enableSecureRefresh: true,
      validateRefreshResponse: true,
      enableRefreshMonitoring: true,
      enableDebugLogging: false,
      ...config
    };
  }

  /**
   * Initialize the token rotation service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (this.config.enableDebugLogging) {
        console.log('🔄 Initializing Token Rotation Service...');
      }

      // Initialize secure token storage
      await secureTokenStorage.initialize();

      // Start monitoring if enabled
      if (this.config.enableRefreshMonitoring) {
        await this.startRefreshMonitoring();
      }

      // Start rotation timer if enabled
      if (this.config.enableTokenRotation) {
        this.startRotationTimer();
      }

      this.isInitialized = true;

      if (this.config.enableDebugLogging) {
        console.log('✅ Token Rotation Service initialized successfully');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Token Rotation Service:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: TokenRefreshEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: TokenRefreshEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: TokenRefreshEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in token refresh event listener:', error);
      }
    });
  }

  /**
   * Start refresh monitoring
   */
  private async startRefreshMonitoring(): Promise<void> {
    // Check tokens immediately
    await this.checkAndRefreshTokens();

    // Set up periodic checking
    this.refreshTimer = setInterval(async () => {
      await this.checkAndRefreshTokens();
    }, 60000); // Check every minute
  }

  /**
   * Start rotation timer
   */
  private startRotationTimer(): void {
    this.rotationTimer = setInterval(async () => {
      await this.performTokenRotation();
    }, this.config.rotationInterval);
  }

  /**
   * Check and refresh tokens if needed
   */
  private async checkAndRefreshTokens(): Promise<void> {
    if (this.isRefreshing) return;

    try {
      const validation = await secureTokenStorage.validateTokens();
      
      if (!validation.isValid) {
        if (validation.isExpired) {
          this.emitEvent({
            type: 'token_expired',
            data: {
              expiredAt: new Date().toISOString(),
              action: 'refresh'
            }
          });
        }
        return;
      }

      // Check if refresh is needed
      if (validation.needsRefresh || validation.expiresIn <= this.config.refreshThreshold) {
        this.emitEvent({
          type: 'refresh_threshold_reached',
          data: {
            expiresIn: validation.expiresIn,
            willRefresh: this.config.enableAutoRefresh
          }
        });

        if (this.config.enableAutoRefresh) {
          await this.refreshTokens('automatic');
        }
      }
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Error checking tokens:', error);
      }
    }
  }

  /**
   * Refresh tokens manually
   */
  async refreshTokens(reason: string = 'manual'): Promise<boolean> {
    if (this.isRefreshing) {
      if (this.config.enableDebugLogging) {
        console.log('Token refresh already in progress');
      }
      return false;
    }

    this.isRefreshing = true;
    this.refreshAttempts = 0;
    const startTime = Date.now();

    try {
      this.emitEvent({
        type: 'refresh_started',
        data: {
          reason,
          timestamp: new Date().toISOString()
        }
      });

      const success = await this.performTokenRefresh();
      
      if (success) {
        const duration = Date.now() - startTime;
        this.lastRefreshTime = new Date().toISOString();
        
        const tokens = await secureTokenStorage.retrieveTokens();
        
        this.emitEvent({
          type: 'refresh_completed',
          data: {
            duration,
            newExpiresAt: tokens?.expiresAt || new Date().toISOString()
          }
        });

        if (this.config.enableDebugLogging) {
          console.log(`✅ Token refresh completed in ${duration}ms`);
        }
      }

      return success;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      return false;
    } finally {
      this.isRefreshing = false;
      this.refreshAttempts = 0;
    }
  }

  /**
   * Perform token refresh with retry logic
   */
  private async performTokenRefresh(): Promise<boolean> {
    while (this.refreshAttempts < this.config.refreshRetryAttempts) {
      this.refreshAttempts++;

      try {
        const tokens = await secureTokenStorage.retrieveTokens();
        
        if (!tokens?.refreshToken) {
          throw new Error('No refresh token available');
        }

        // Make refresh request
        const response = await this.makeRefreshRequest(tokens.refreshToken);
        
        if (this.config.validateRefreshResponse) {
          this.validateRefreshResponse(response);
        }

        // Calculate new expiration time
        const expiresAt = new Date(Date.now() + (response.expires_in * 1000)).toISOString();

        // Update tokens in secure storage
        if (response.refresh_token) {
          // Full token rotation
          const newTokenData: StoredTokenData = {
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            expiresAt,
            tokenType: response.token_type,
            scope: response.scope,
            userId: tokens.userId,
            createdAt: tokens.createdAt,
            lastRefreshed: new Date().toISOString()
          };
          
          await secureTokenStorage.storeTokens(newTokenData);
        } else {
          // Just update access token
          await secureTokenStorage.updateAccessToken(response.access_token, expiresAt);
        }

        return true;
      } catch (error) {
        const isLastAttempt = this.refreshAttempts >= this.config.refreshRetryAttempts;
        
        this.emitEvent({
          type: 'refresh_failed',
          data: {
            error: error instanceof Error ? error.message : 'Unknown error',
            attempt: this.refreshAttempts,
            willRetry: !isLastAttempt
          }
        });

        if (isLastAttempt) {
          throw error;
        }

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, this.config.refreshRetryDelay));
      }
    }

    return false;
  }

  /**
   * Make refresh request to server
   */
  private async makeRefreshRequest(refreshToken: string): Promise<TokenRefreshResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeoutDuration);

    try {
      const response = await fetch(this.config.refreshEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
          grant_type: 'refresh_token'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Validate refresh response
   */
  private validateRefreshResponse(response: TokenRefreshResponse): void {
    const required = ['access_token', 'expires_in', 'token_type'];
    const missing = required.filter(field => !response[field as keyof TokenRefreshResponse]);
    
    if (missing.length > 0) {
      throw new Error(`Invalid refresh response: missing ${missing.join(', ')}`);
    }

    if (typeof response.expires_in !== 'number' || response.expires_in <= 0) {
      throw new Error('Invalid expires_in value in refresh response');
    }
  }

  /**
   * Perform token rotation
   */
  private async performTokenRotation(): Promise<void> {
    if (!this.config.enableTokenRotation) return;

    try {
      const tokens = await secureTokenStorage.retrieveTokens();
      
      if (!tokens) return;

      // Check if token is old enough for rotation
      const tokenAge = Date.now() - new Date(tokens.createdAt).getTime();
      
      if (tokenAge >= this.config.maxTokenAge) {
        const success = await this.refreshTokens('rotation');
        
        if (success) {
          this.emitEvent({
            type: 'rotation_completed',
            data: {
              oldTokenId: tokens.userId,
              newTokenId: tokens.userId
            }
          });

          if (this.config.enableDebugLogging) {
            console.log('🔄 Token rotation completed');
          }
        }
      }
    } catch (error) {
      if (this.config.enableDebugLogging) {
        console.error('Token rotation failed:', error);
      }
    }
  }

  /**
   * Force token refresh
   */
  async forceRefresh(): Promise<boolean> {
    return await this.refreshTokens('forced');
  }

  /**
   * Check if tokens need refresh
   */
  async needsRefresh(): Promise<boolean> {
    const validation = await secureTokenStorage.validateTokens();
    return validation.needsRefresh || validation.expiresIn <= this.config.refreshThreshold;
  }

  /**
   * Get refresh status
   */
  getRefreshStatus(): {
    isRefreshing: boolean;
    lastRefreshTime: string | null;
    refreshAttempts: number;
    nextRefreshCheck?: string;
  } {
    return {
      isRefreshing: this.isRefreshing,
      lastRefreshTime: this.lastRefreshTime,
      refreshAttempts: this.refreshAttempts,
      nextRefreshCheck: this.refreshTimer ? new Date(Date.now() + 60000).toISOString() : undefined
    };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<TokenRotationConfig>): void {
    this.config = { ...this.config, ...updates };
    
    // Restart timers if intervals changed
    if (updates.rotationInterval && this.config.enableTokenRotation) {
      if (this.rotationTimer) {
        clearInterval(this.rotationTimer);
      }
      this.startRotationTimer();
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): TokenRotationConfig {
    return { ...this.config };
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.rotationTimer) {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }

    this.eventListeners.clear();
    this.isInitialized = false;

    if (this.config.enableDebugLogging) {
      console.log('🧹 Token Rotation Service cleaned up');
    }
  }
}

// Export singleton instance
export const tokenRotationService = new TokenRotationService();
